import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import TranslationButton from '../../components/ui/TranslationButton';
import PropertyVideoPlayer from './components/PropertyVideoPlayer';
import PropertyDetails from './components/PropertyDetails';
import CommuneContext from './components/CommuneContext';
import SchedulingSection from './components/SchedulingSection';
import InquiryForm from './components/InquiryForm';
import RelatedProperties from './components/RelatedProperties';
import { getProperty, getRelatedProperties } from '../../services/propertyService';
import { prefetchObjAssets } from '../../services/objAssetService';
import { trackPropertyView } from '../../services/userJourneyService';
import { gaTrackPropertyView } from '../../services/gaAnalyticsService';
import { Shield, Home, AlertCircle, Phone, Calendar, ArrowRight, Play } from 'lucide-react';

// Helper to detect GLB models in property videos
const hasGlbModel = (property) => {
  if (!property) return false;
  const videos = Array.isArray(property?.videos) ? property?.videos : [];
  return videos?.some((v) => {
    const raw = v?.url || (typeof v === 'string' ? v : '');
    const url = (typeof raw === 'string' ? raw : '')?.toLowerCase()?.split('?')?.[0];
    return url?.endsWith('.glb') || url?.endsWith('.gltf');
  });
};

const IndividualPropertyPage = () => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get property ID from URL params or search params (backward compatibility)
  const currentPropertyId = propertyId || searchParams?.get('id');
  
  // State management
  const [property, setProperty] = useState(null);
  const [relatedProperties, setRelatedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load property data
  useEffect(() => {
    const loadPropertyData = async () => {
      if (!currentPropertyId) {
        setError('Aucun ID de propriété fourni');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const propertyResponse = await getProperty(currentPropertyId);
        
        if (propertyResponse?.error) {
          if (propertyResponse?.error?.includes('uuid') || propertyResponse?.error?.includes('invalid input syntax')) {
            setError('Format d\'ID de propriété invalide. Les liens de propriété doivent contenir un identifiant valide.');
          } else {
            setError(propertyResponse?.error);
          }
          return;
        }

        if (!propertyResponse?.data) {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (!uuidRegex?.test(currentPropertyId)) {
            setError('Format d\'ID de propriété invalide. Les liens de propriété doivent contenir un identifiant valide.');
          } else {
            setError('Cette propriété n\'existe pas ou n\'est plus disponible.');
          }
          return;
        }

        const propertyData = propertyResponse?.data;
        setProperty(propertyData);

        trackPropertyView(propertyData);
        gaTrackPropertyView(propertyData);

        const objUrls = (Array.isArray(propertyData?.videos) ? propertyData?.videos : [])
          ?.filter(v => v?.type === 'obj' || v?.url?.toLowerCase()?.includes('.obj'))
          ?.map(v => v?.url)
          ?.filter(Boolean);
        if (objUrls?.length > 0) {
          prefetchObjAssets(objUrls);
        }

        if (propertyData?.commune_id && propertyData?.property_type) {
          const relatedResponse = await getRelatedProperties(
            currentPropertyId,
            propertyData?.commune_id,
            propertyData?.property_type
          );
          
          if (relatedResponse?.data && Array.isArray(relatedResponse?.data)) {
            setRelatedProperties(relatedResponse?.data);
          } else {
            setRelatedProperties([]);
          }
        }

      } catch (err) {
        console.error('Error loading property:', err);
        if (err?.code === 'PGRST116') {
          setError('Cette propriété n\'existe pas ou n\'est plus disponible.');
        } else if (err?.code === '22P02') {
          setError('Format d\'ID de propriété invalide. Veuillez vérifier le lien.');
        } else {
          setError('Erreur lors du chargement de la propriété. Veuillez réessayer.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadPropertyData();
  }, [currentPropertyId]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col border-none outline-none">
        <Header className="bg-transparent border-none" />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-white/10 border-t-brand-gold mx-auto mb-6"></div>
            <p className="text-sm font-light text-white/50 tracking-widest uppercase">Chargement de la propriété...</p>
          </div>
        </main>
      </div>
    );
  }

  // Error state or no property ID
  if (error || !currentPropertyId) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col border-none outline-none">
        <Header className="bg-transparent border-none" />
        <main className="flex-grow flex items-center justify-center p-6">
          <div className="text-center max-w-md bg-[#111111] p-10 rounded-3xl shadow-2xl">
            <div className="w-16 h-16 bg-red-500/10 text-red-400 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={32} />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-4">
              Propriété non trouvée
            </h1>
            <p className="text-sm text-white/50 mb-8 leading-relaxed font-light">
              {error || 'Aucun ID de propriété fourni'}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/property-search-listings')}
                className="w-full h-12 rounded-full font-bold bg-brand-gold text-black hover:bg-yellow-400 transition-colors border-none"
              >
                Parcourir les propriétés
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full h-12 rounded-full font-medium bg-white/5 text-white hover:bg-white/10 transition-colors border-none"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // No property found
  if (!property) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col border-none outline-none">
        <Header className="bg-transparent border-none" />
        <main className="flex-grow flex items-center justify-center p-6">
          <div className="text-center max-w-md bg-[#111111] p-10 rounded-3xl shadow-2xl">
            <div className="w-16 h-16 bg-white/5 text-white/40 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Home size={32} />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-4">
              Propriété indisponible
            </h1>
            <p className="text-sm text-white/50 mb-8 leading-relaxed font-light">
              Cette propriété n'est plus disponible ou a été retirée du marché.
            </p>
            <button
              onClick={() => navigate('/property-search-listings')}
              className="w-full h-12 rounded-full font-bold bg-white text-black hover:bg-gray-200 transition-colors border-none"
            >
              Voir d'autres biens
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Format price for display
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <>
      <Helmet>
        <title>{property?.title} - {property?.communes?.name} | Kapital States</title>
        <meta 
          name="description" 
          content={`${property?.title} à ${property?.address}, ${property?.communes?.name}. ${property?.surface_area}m², ${property?.bedrooms} chambres. Prix: ${formatPrice(property?.price)}`} 
        />
        <meta 
          name="keywords" 
          content={`${property?.property_type}, ${property?.communes?.name}, immobilier Abidjan, ${property?.bedrooms} chambres, ${property?.surface_area}m²`} 
        />
        <meta property="og:title" content={`${property?.title} - Kapital States`} />
        <meta 
          property="og:description" 
          content={`Découvrez cette ${property?.property_type?.toLowerCase()} de ${property?.surface_area}m² avec ${property?.bedrooms} chambres à ${property?.communes?.name}`} 
        />
        <meta property="og:image" content={property?.images?.[0] || '/assets/images/no_image.png'} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://kapitalstates.com/property/${property?.id}`} />
      </Helmet>
      
      {/* 
        Le conteneur principal: 
        Fond noir absolu (#050505), zéro bordure.
      */}
      <div className="min-h-screen bg-[#050505] text-white flex flex-col relative overflow-x-hidden border-none outline-none">
        
        {/* Glows d'ambiance extrêmement subtils */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute top-0 right-1/4 w-[600px] h-[400px] bg-brand-gold/5 rounded-full blur-[150px] -translate-y-1/2" />
        </div>

        <Header className="bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50" />
        
        <main className="relative z-10 w-full">
          
          {/* Hero Section with Video */}
          <section className="pt-8 pb-4 lg:pt-12 lg:pb-8">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              
              {/* Le composant vidéo. Assurez-vous qu'il n'a pas de bordure blanche dure dans son propre code. */}
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#111111] border-none">
                <PropertyVideoPlayer property={property} />
              </div>

              {/* Badge Vérifié par Notaire */}
              {property?.is_notary_verified &&
                <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-emerald-500/10 border-none shadow-lg shadow-emerald-500/5">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-400/20 flex items-center justify-center">
                    <Shield size={16} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-400">Vérifié par Notaire</p>
                    <p className="text-xs text-emerald-400/70 font-light hidden sm:block">Conformité juridique validée par Kapital States.</p>
                  </div>
                </div>
              }
            </div>
          </section>

          {/* 3D Immersive Tour CTA — Apple Style Banner */}
          {hasGlbModel(property) && (
            <section className="py-2">
              <div className="max-w-7xl mx-auto px-4 lg:px-6">
                <div
                  className="bg-gradient-to-r from-[#1A1A1A] to-[#111111] rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden group border-none"
                >
                  {/* Effet lumineux interne au survol */}
                  <div className="absolute inset-0 bg-brand-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  
                  <div className="flex items-center gap-5 relative z-10 w-full md:w-auto">
                    <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0 shadow-inner">
                      <Play size={24} className="text-brand-gold ml-1" />
                    </div>
                    <div>
                      <h3 className="text-brand-gold font-heading font-bold tracking-widest text-sm uppercase mb-1">
                        Visite 3D Immersive
                      </h3>
                      <p className="text-white/50 text-sm font-light">
                        Explorez chaque pièce à 360° · Disponible sur Mobile & Desktop
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => navigate(`/visite-3d/${currentPropertyId}`)}
                    className="w-full md:w-auto flex-shrink-0 flex items-center justify-center gap-2 bg-brand-gold text-black font-bold px-8 py-4 rounded-full transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-brand-gold/20 border-none relative z-10"
                  >
                    Entrer dans la visite
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Main Content Grid */}
          <section className="py-8">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                
                {/* Colonne Gauche - Détails */}
                <div className="lg:col-span-2 space-y-12">
                  {/* Note: Ces sous-composants devront idéalement utiliser bg-[#111] et border-none s'ils ont des cadres */}
                  <div className="border-none">
                    <PropertyDetails property={property} />
                  </div>
                  <div className="border-none">
                    <CommuneContext commune={property?.communes} property={property} />
                  </div>
                </div>

                {/* Colonne Droite - Actions */}
                <div className="space-y-8">
                  {/* Note: Idem, utiliser bg-[#111] et border-none en interne */}
                  <div className="sticky top-28 space-y-8">
                    <div className="border-none shadow-2xl rounded-3xl overflow-hidden">
                      <InquiryForm property={property} />
                    </div>
                    <div className="border-none shadow-2xl rounded-3xl overflow-hidden">
                      <SchedulingSection property={property} />
                    </div>
                  </div>
                </div>
                
              </div>
            </div>
          </section>

          {/* Related Properties */}
          <section className="py-12 border-t border-white/5 mt-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              {/* Le composant RelatedProperties devra idéalement hériter du style sombre sans bordure des autres cartes */}
              <RelatedProperties 
                currentProperty={property} 
                relatedProperties={relatedProperties}
              />
            </div>
          </section>

          {/* Bottom CTA Section - Premium Dark Mode */}
          <section className="py-24 relative overflow-hidden">
            {/* Halo central */}
            <div className="absolute inset-0 bg-brand-gold/5 blur-[100px] pointer-events-none" />
            
            <div className="max-w-3xl mx-auto px-4 lg:px-6 text-center relative z-10">
              <p className="text-xs font-bold tracking-widest text-brand-gold uppercase mb-6 font-body">
                Consultation gratuite
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white tracking-tight leading-tight mb-6">
                Prêt à visiter cette propriété ?
              </h2>
              <p className="text-lg text-white/50 font-light leading-relaxed mb-12 max-w-xl mx-auto">
                Nos agents experts vous accompagnent dans votre projet immobilier. Disponibles 7j/7, réponse sous 2h.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`tel:${property?.agents?.phone || '+225 21 50 00 53 18'}`}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-black bg-brand-gold hover:bg-yellow-400 transition-colors shadow-lg shadow-brand-gold/20 no-underline"
                >
                  <Phone size={18} /> Appeler maintenant
                </a>
                <button className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-white bg-white/5 hover:bg-white/10 transition-colors border-none">
                  <Calendar size={18} /> Réserver une visite
                </button>
              </div>
            </div>
          </section>
          
        </main>

        {/* Translation Button - Floating */}
        <TranslationButton />
      </div>
    </>
  );
};

export default IndividualPropertyPage;